import React, { useState } from 'react'
import { Plus, Trash2, ChevronRight } from 'lucide-react'

// ─── diff helpers (proposed vs. current parish data) ───────────────────────────
const normStr = (v) => (typeof v === 'string' ? v.trim() : '')
const staffKey = (s) => normStr(s?.name).toLowerCase()
const staffEqual = (a, b) =>
  ['title', 'name', 'email', 'phone'].every((k) => normStr(a?.[k]) === normStr(b?.[k]))

const dayKey = (d) => normStr(d?.day).toLowerCase()
const canonTime = (t) =>
  JSON.stringify({
    time: normStr(t?.time),
    startTime: normStr(t?.startTime),
    endTime: normStr(t?.endTime),
    note: normStr(t?.note),
  })
const canonDay = (d) => `${dayKey(d)}|${(d?.times ?? []).map(canonTime).join(';')}`
const daysEqual = (a, b) => canonDay(a) === canonDay(b)

// Returns [{ index, status }] where status is 'new' | 'changed' | 'unchanged'.
// An entry is 'new' if its key isn't in `current`, 'unchanged' if an equal match
// exists, otherwise 'changed'.
export function partitionByStatus(proposed, current, keyFn, equalFn) {
  const byKey = new Map()
  ;(Array.isArray(current) ? current : []).forEach((item) => byKey.set(keyFn(item), item))
  return (Array.isArray(proposed) ? proposed : []).map((item, index) => {
    const match = byKey.get(keyFn(item))
    let status
    if (match === undefined) status = 'new'
    else if (equalFn(item, match)) status = 'unchanged'
    else status = 'changed'
    return { index, status }
  })
}

function StatusBadge({ status }) {
  if (status === 'new') {
    return (
      <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
        New
      </span>
    )
  }
  if (status === 'changed') {
    return (
      <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-50 border border-blue-200 rounded px-1.5 py-0.5">
        Updated
      </span>
    )
  }
  return null
}

function ExistingDisclosure({ count, children }) {
  const [open, setOpen] = useState(false)
  if (!count) return null
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-1 text-xs text-stone-500 hover:text-stone-800"
      >
        <ChevronRight className={`h-3.5 w-3.5 transition-transform ${open ? 'rotate-90' : ''}`} />
        {open ? 'Hide' : 'Show'} {count} existing (unchanged)
      </button>
      {open && <div className="mt-3 space-y-3 opacity-70">{children}</div>}
    </div>
  )
}

// ─── shared styles ────────────────────────────────────────────────────────────
const inputCls =
  'w-full rounded border border-stone-300 px-3 py-2 text-sm text-stone-800 bg-white focus:outline-none focus:ring-1 focus:ring-stone-400 disabled:bg-stone-50 disabled:text-stone-500'
const labelCls = 'block text-xs font-medium text-stone-500 mb-1'

function Field({ label, children }) {
  return (
    <label className="block">
      <span className={labelCls}>{label}</span>
      {children}
    </label>
  )
}

function TextInput({ value, onChange, disabled, placeholder, type = 'text' }) {
  return (
    <input
      type={type}
      className={inputCls}
      value={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function TextArea({ value, onChange, disabled, rows = 4 }) {
  return (
    <textarea
      className={inputCls}
      rows={rows}
      value={value ?? ''}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  )
}

function AddButton({ onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="inline-flex items-center gap-1 text-xs font-medium text-stone-600 hover:text-stone-900"
    >
      <Plus className="h-3.5 w-3.5" /> {children}
    </button>
  )
}

function RemoveButton({ onClick, label = 'Remove' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="text-stone-400 hover:text-red-600 shrink-0"
    >
      <Trash2 className="h-4 w-4" />
    </button>
  )
}

// ─── ISO <-> datetime-local ───────────────────────────────────────────────────
function isoToLocalInput(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`
}
function localInputToIso(local) {
  if (!local) return ''
  const d = new Date(local)
  return isNaN(d.getTime()) ? '' : d.toISOString()
}

// ─── per-type editors ─────────────────────────────────────────────────────────
function AnnouncementEditor({ value, onChange, disabled }) {
  const set = (k, v) => onChange({ ...value, [k]: v })
  return (
    <div className="space-y-3">
      <Field label="Title">
        <TextInput value={value.title} onChange={(v) => set('title', v)} disabled={disabled} />
      </Field>
      <Field label="Content">
        <TextArea value={value.content} onChange={(v) => set('content', v)} disabled={disabled} />
      </Field>
    </div>
  )
}

function EventEditor({ value, onChange, disabled }) {
  const set = (k, v) => onChange({ ...value, [k]: v })
  return (
    <div className="space-y-3">
      <Field label="Title">
        <TextInput value={value.title} onChange={(v) => set('title', v)} disabled={disabled} />
      </Field>
      <Field label="Description">
        <TextArea value={value.description} onChange={(v) => set('description', v)} disabled={disabled} rows={3} />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Start">
          <TextInput
            type="datetime-local"
            value={isoToLocalInput(value.startTime)}
            onChange={(v) => set('startTime', localInputToIso(v))}
            disabled={disabled}
          />
        </Field>
        <Field label="End">
          <TextInput
            type="datetime-local"
            value={isoToLocalInput(value.endTime)}
            onChange={(v) => set('endTime', localInputToIso(v))}
            disabled={disabled}
          />
        </Field>
      </div>
      <Field label="Location name">
        <TextInput value={value.locationName} onChange={(v) => set('locationName', v)} disabled={disabled} />
      </Field>
      <Field label="Location address">
        <TextInput value={value.locationAddress} onChange={(v) => set('locationAddress', v)} disabled={disabled} />
      </Field>
    </div>
  )
}

const PARISH_FIELD_LABELS = {
  description: 'Description',
  briefBio: 'Brief bio',
  phoneNumber: 'Phone number',
  email: 'Email',
}

function ParishFieldEditor({ value, onChange, disabled }) {
  const multiline = value.field === 'description' || value.field === 'briefBio'
  return (
    <Field label={PARISH_FIELD_LABELS[value.field] ?? value.field}>
      {multiline ? (
        <TextArea value={value.value} onChange={(v) => onChange({ ...value, value: v })} disabled={disabled} rows={3} />
      ) : (
        <TextInput value={value.value} onChange={(v) => onChange({ ...value, value: v })} disabled={disabled} />
      )}
    </Field>
  )
}

// Shared editor for mass_times / confession_times. `timeFields` describes the
// per-time-slot inputs (mass: [time, note]; confession: [startTime, endTime, note]).
function DayTimesEditor({ value, onChange, disabled, current, listKey, timeFields }) {
  const days = Array.isArray(value[listKey]) ? value[listKey] : []
  const setDays = (next) => onChange({ ...value, [listKey]: next })
  const updateDay = (i, patch) =>
    setDays(days.map((d, idx) => (idx === i ? { ...d, ...patch } : d)))
  const updateTime = (di, ti, patch) => {
    const day = days[di]
    const times = (day.times ?? []).map((t, idx) => (idx === ti ? { ...t, ...patch } : t))
    updateDay(di, { times })
  }

  // Only diff against current when editing a pending proposal; done proposals and
  // missing current data fall back to showing every day flat.
  const useDiff = !disabled && current != null
  const parts = useDiff
    ? partitionByStatus(days, current, dayKey, daysEqual)
    : days.map((_, index) => ({ index, status: null }))
  const changed = parts.filter((p) => p.status !== 'unchanged')
  const unchanged = parts.filter((p) => p.status === 'unchanged')

  const renderDay = (di, status) => {
    const day = days[di]
    return (
      <div key={di} className="rounded border border-stone-200 p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="flex-1">
            <Field label="Day">
              <TextInput value={day.day} onChange={(v) => updateDay(di, { day: v })} disabled={disabled} placeholder="e.g. Saturday" />
            </Field>
          </div>
          <StatusBadge status={status} />
          {!disabled && (
            <RemoveButton onClick={() => setDays(days.filter((_, idx) => idx !== di))} label="Remove day" />
          )}
        </div>

        <div className="space-y-2 pl-1">
          {(day.times ?? []).map((t, ti) => (
            <div key={ti} className="flex items-end gap-2">
              {timeFields.map((f) => (
                <div key={f.key} className="flex-1">
                  <Field label={f.label}>
                    <TextInput
                      value={t[f.key]}
                      onChange={(v) => updateTime(di, ti, { [f.key]: v })}
                      disabled={disabled}
                      placeholder={f.placeholder}
                    />
                  </Field>
                </div>
              ))}
              {!disabled && (
                <div className="pb-2">
                  <RemoveButton
                    onClick={() => updateDay(di, { times: (day.times ?? []).filter((_, idx) => idx !== ti) })}
                    label="Remove time"
                  />
                </div>
              )}
            </div>
          ))}
          {!disabled && (
            <AddButton onClick={() => updateDay(di, { times: [...(day.times ?? []), {}] })}>Add time</AddButton>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {changed.map((p) => renderDay(p.index, p.status))}
      {!disabled && (
        <AddButton onClick={() => setDays([...days, { day: '', times: [{}] }])}>Add day</AddButton>
      )}
      <ExistingDisclosure count={unchanged.length}>
        {unchanged.map((p) => renderDay(p.index, 'unchanged'))}
      </ExistingDisclosure>
    </div>
  )
}

function MassTimesEditor(props) {
  return (
    <DayTimesEditor
      {...props}
      listKey="massTimes"
      timeFields={[
        { key: 'time', label: 'Time', placeholder: 'e.g. 5:00 PM' },
        { key: 'note', label: 'Note (optional)', placeholder: 'e.g. Vigil' },
      ]}
    />
  )
}

function ConfessionTimesEditor(props) {
  return (
    <DayTimesEditor
      {...props}
      listKey="confessionTimes"
      timeFields={[
        { key: 'startTime', label: 'Start', placeholder: 'e.g. 3:00 PM' },
        { key: 'endTime', label: 'End', placeholder: 'e.g. 4:00 PM' },
        { key: 'note', label: 'Note (optional)', placeholder: '' },
      ]}
    />
  )
}

function StaffEditor({ value, onChange, disabled, current }) {
  const staff = Array.isArray(value.parishStaff) ? value.parishStaff : []
  const setStaff = (next) => onChange({ ...value, parishStaff: next })
  const update = (i, patch) => setStaff(staff.map((s, idx) => (idx === i ? { ...s, ...patch } : s)))

  const useDiff = !disabled && current != null
  const parts = useDiff
    ? partitionByStatus(staff, current, staffKey, staffEqual)
    : staff.map((_, index) => ({ index, status: null }))
  const changed = parts.filter((p) => p.status !== 'unchanged')
  const unchanged = parts.filter((p) => p.status === 'unchanged')

  const renderMember = (i, status) => {
    const member = staff[i]
    return (
      <div key={i} className="rounded border border-stone-200 p-3">
        <div className="flex items-start gap-2">
          <div className="grid grid-cols-2 gap-3 flex-1">
            <Field label="Title">
              <TextInput value={member.title} onChange={(v) => update(i, { title: v })} disabled={disabled} placeholder="e.g. Pastor" />
            </Field>
            <Field label="Name">
              <TextInput value={member.name} onChange={(v) => update(i, { name: v })} disabled={disabled} placeholder="e.g. Fr. John" />
            </Field>
            <Field label="Email (optional)">
              <TextInput type="email" value={member.email} onChange={(v) => update(i, { email: v })} disabled={disabled} />
            </Field>
            <Field label="Phone (optional)">
              <TextInput value={member.phone} onChange={(v) => update(i, { phone: v })} disabled={disabled} />
            </Field>
          </div>
          <div className="flex flex-col items-end gap-2 pt-1">
            <StatusBadge status={status} />
            {!disabled && (
              <RemoveButton onClick={() => setStaff(staff.filter((_, idx) => idx !== i))} label="Remove staff member" />
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {changed.map((p) => renderMember(p.index, p.status))}
      {!disabled && (
        <AddButton onClick={() => setStaff([...staff, { title: '', name: '' }])}>Add staff member</AddButton>
      )}
      <ExistingDisclosure count={unchanged.length}>
        {unchanged.map((p) => renderMember(p.index, 'unchanged'))}
      </ExistingDisclosure>
    </div>
  )
}

const EDITORS = {
  announcement: AnnouncementEditor,
  event: EventEditor,
  parish_field: ParishFieldEditor,
  mass_times: MassTimesEditor,
  confession_times: ConfessionTimesEditor,
  staff: StaffEditor,
}

export function ProposalEditor({ proposal, value, onChange, disabled, current }) {
  const Editor = EDITORS[proposal.type]
  if (!Editor) {
    return (
      <pre className="text-xs bg-stone-50 border border-stone-200 rounded p-3 overflow-x-auto text-stone-600">
        {JSON.stringify(value, null, 2)}
      </pre>
    )
  }
  return <Editor value={value} onChange={onChange} disabled={disabled} current={current} />
}

// Minimal required-field validation used to gate the Approve button.
export function isPayloadValid(type, payload) {
  if (!payload || typeof payload !== 'object') return false
  const nonEmpty = (v) => typeof v === 'string' && v.trim().length > 0
  switch (type) {
    case 'announcement':
      return nonEmpty(payload.title) && nonEmpty(payload.content)
    case 'event':
      return (
        nonEmpty(payload.title) &&
        nonEmpty(payload.startTime) &&
        nonEmpty(payload.endTime) &&
        nonEmpty(payload.locationName) &&
        nonEmpty(payload.locationAddress)
      )
    case 'parish_field':
      return nonEmpty(payload.field) && nonEmpty(payload.value)
    case 'mass_times':
      return (
        Array.isArray(payload.massTimes) &&
        payload.massTimes.length > 0 &&
        payload.massTimes.every(
          (d) =>
            nonEmpty(d.day) &&
            Array.isArray(d.times) &&
            d.times.length > 0 &&
            d.times.every((t) => nonEmpty(t.time)),
        )
      )
    case 'confession_times':
      return (
        Array.isArray(payload.confessionTimes) &&
        payload.confessionTimes.length > 0 &&
        payload.confessionTimes.every(
          (d) =>
            nonEmpty(d.day) &&
            Array.isArray(d.times) &&
            d.times.length > 0 &&
            d.times.every((t) => nonEmpty(t.startTime) && nonEmpty(t.endTime)),
        )
      )
    case 'staff':
      return (
        Array.isArray(payload.parishStaff) &&
        payload.parishStaff.length > 0 &&
        payload.parishStaff.every((s) => nonEmpty(s.title) && nonEmpty(s.name))
      )
    default:
      return true
  }
}
